import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';

import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
import UsersPage from './pages/UsersPage';
import BillingPage from './pages/BillingPage';
import AccountsPage from './pages/AccountsPage';
import AccountDetailPage from './pages/AccountDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';

function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('nca_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const toggle = useCallback(() => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem('nca_sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'radial-gradient(ellipse 1300px 640px at 50% -220px, rgba(59,130,246,.08), transparent 62%), radial-gradient(ellipse 900px 500px at 100% 0%, rgba(59,130,246,.04), transparent 60%), #0A0A0B' }}>
      <Sidebar collapsed={collapsed} onToggle={toggle} />
      <div className={`transition-[padding] duration-200 ease-out pl-0 ${collapsed ? 'md:pl-[76px]' : 'md:pl-[248px]'}`}>
        {children}
      </div>
    </div>
  );
}

// The whole portal is super-admin only. A logged-in non-admin sees an access screen.
function RequireSuperAdmin({ children }) {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div style={{ maxWidth: 420, textAlign: 'center', background: '#161619', border: '1px solid #26262B', borderRadius: 16, padding: '40px 32px' }}>
          <div style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 22, color: '#FAFAFA', marginBottom: 8 }}>Access restricted</div>
          <div style={{ fontSize: 14, color: '#A1A1AA', lineHeight: 1.6 }}>This console is for platform administrators only. Your account doesn't have admin access.</div>
        </div>
      </div>
    );
  }
  return children;
}

function ProtectedLayout({ children }) {
  return (
    <ProtectedRoute>
      <RequireSuperAdmin><AppLayout>{children}</AppLayout></RequireSuperAdmin>
    </ProtectedRoute>
  );
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<RootRoute />} />
          <Route path="/dashboard"          element={<ProtectedLayout><DashboardPage /></ProtectedLayout>} />
          <Route path="/organizations"      element={<ProtectedLayout><OrganizationsPage /></ProtectedLayout>} />
          <Route path="/organizations/:id"  element={<ProtectedLayout><OrganizationDetailPage /></ProtectedLayout>} />
          <Route path="/users"              element={<ProtectedLayout><UsersPage /></ProtectedLayout>} />
          <Route path="/billing"            element={<ProtectedLayout><BillingPage /></ProtectedLayout>} />
          <Route path="/accounts"           element={<ProtectedLayout><AccountsPage /></ProtectedLayout>} />
          <Route path="/accounts/:id"       element={<ProtectedLayout><AccountDetailPage /></ProtectedLayout>} />
          <Route path="/notifications"      element={<ProtectedLayout><NotificationsPage /></ProtectedLayout>} />
          <Route path="/settings"           element={<ProtectedLayout><SettingsPage /></ProtectedLayout>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
